import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useBlockOutDatesList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refBlockOutDatesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      label: 'name',
      sortable: true,
    },
    {
      key: 'datePeriod',
      label: 'Date range',
      sortable: true,
    },
    {
      key: 'activityTypes',
      label: 'Applies to',
      // sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage || process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalBlockOutDates = ref(0)
  const currentPage = ref(route.query.page || 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search || '')
  const sortBy = ref(route.query.sortBy || 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref(route.query.status || null)
  const dateRange = ref(route.query.dateRange || '')
  const appliesTo = ref([])
  if (Array.isArray(route.query.activityType)) {
    appliesTo.value = route.query.activityType
  } else if (route.query.activityType) {
    appliesTo.value = [route.query.activityType]
  }
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refBlockOutDatesTable.value ? refBlockOutDatesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalBlockOutDates.value,
    }
  })

  const refetchData = () => {
    refBlockOutDatesTable.value.refresh()
  }

  watch([searchQuery, statusFilter, dateRange, appliesTo], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchBlockOutDates = (ctx, callback) => {
    store
      .dispatch('app-block-out-dates/fetchBlockOutDates', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        dateRange: dateRange.value,
        appliesTo: appliesTo.value,
      },
      { root: true })
      .then(response => {
        const { blockOutDates, total } = response.data
        currentDataLength.value = blockOutDates.length
        callback(blockOutDates)
        totalBlockOutDates.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              dateRange: dateRange.value,
              appliesTo: appliesTo.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteBlockOutDate = () => {
    store
      .dispatch('app-block-out-dates/deleteBlockOutDate', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message || '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveBlockOutDateStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'danger'
    return 'primary'
  }

  const resolveActivityTypeNameVariant = name => {
    if (name === 'prayers') return 'success'
    return 'primary'
  }

  return {
    fetchBlockOutDates,
    tableColumns,
    perPage,
    currentPage,
    totalBlockOutDates,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refBlockOutDatesTable,

    resolveBlockOutDateStatusVariant,
    resolveActivityTypeNameVariant,
    refetchData,

    toDeletedID,
    deleteBlockOutDate,
    currentDataLength,

    // Extra Filters
    statusFilter,
    dateRange,
    appliesTo,
  }
}
